import { sessionApiClient } from "@planningcenter/cc-api-client"
import { useSuspenseQuery } from "@tanstack/react-query"
import { useFlipperFeatureEnabled } from "source/shared/flipperFeatures"

import { SingularAPIResponseWithIncluded } from "source/shared/types"
import {
  ChannelAPIRecord,
  EpisodeAPIRecordWithIncluded,
  EpisodeResourceAPIRecord,
  EpisodeTimesAPIRecord,
  NoteAPIRecord,
  NoteTemplateAPIRecord,
} from "source/publishing/sermons/shared/types"
import { DELETED_EPISODE } from "source/publishing/sermons/shared/deleted_episode"

type IncludedTypes =
  | ChannelAPIRecord
  | EpisodeResourceAPIRecord
  | EpisodeTimesAPIRecord
type EpisodeSingularAPIResponseWithIncluded = SingularAPIResponseWithIncluded<
  EpisodeAPIRecordWithIncluded,
  IncludedTypes
>
const useEpisodeQueryKey = (episodeId: string) => {
  const ROLLOUT_episode_speakers = useFlipperFeatureEnabled(
    "ROLLOUT_episode_speakers",
  )

  return [
    "episode",
    episodeId,
    [
      "channel",
      "episode_times",
      "episode_resources",
      "person_episode_progress",
      "series",
      "note_template",
      ROLLOUT_episode_speakers && "speakerships.speaker",
    ]
      .filter(Boolean)
      .join(","),
  ]
}

export function isPersisted(resourceId: string) {
  return parseInt(resourceId) > 0
}

export function useMaybeDeletedEpisode(episodeId: string) {
  return useSuspenseQuery({
    queryKey: useEpisodeQueryKey(episodeId),
    queryFn: ({ queryKey }) => {
      const [_, episodeId, include] = queryKey
      return isPersisted(episodeId)
        ? (sessionApiClient
            .get(`/publishing/v2/episodes/${episodeId}`, { include })
            .catch(
              (json: unknown) => json,
            ) as Promise<EpisodeSingularAPIResponseWithIncluded>)
        : new Promise((resolve) => resolve(DELETED_EPISODE))
    },
    refetchOnWindowFocus: false,
  })
}

export function useEpisode(episodeId: string) {
  return useSuspenseQuery({
    queryKey: useEpisodeQueryKey(episodeId),
    queryFn: ({ queryKey }) => {
      const [_, episodeId, include] = queryKey
      return sessionApiClient
        .get(`/publishing/v2/episodes/${episodeId}`, { include })
        .catch(
          (json: unknown) => json,
        ) as Promise<EpisodeSingularAPIResponseWithIncluded>
    },
    refetchOnWindowFocus: false,
  })
}

export function useEpisodeNoteQueryKey(episodeId: string) {
  return ["episodes", episodeId, "note"]
}

export function useNoteQueryKey(noteId: string) {
  return ["notes", noteId, { include: "episode" }]
}

export function useNote(noteId: string) {
  return useSuspenseQuery({
    queryKey: useNoteQueryKey(noteId),
    queryFn: ({ queryKey }) => {
      const [_, noteId, params] = queryKey
      return sessionApiClient
        .get(`/publishing/v2/notes/${noteId}`, params)
        .catch((json: unknown) => json)
    },
    staleTime: Infinity,
  })
}

export function useEpisodeNote(episodeId: string) {
  return useSuspenseQuery({
    queryKey: useEpisodeNoteQueryKey(episodeId),
    queryFn: ({ queryKey }) => {
      const [_, episodeId] = queryKey
      return sessionApiClient
        .get(`/publishing/v2/episodes/${episodeId}/note`)
        .catch((json: unknown) => json)
    },
    select: (response: { data: NoteAPIRecord }) => {
      return { ...response.data.attributes, id: response.data.id }
    },
    staleTime: Infinity,
  })
}

export function useEpisodeNoteTemplateQueryKey(episodeId: string) {
  return ["episodes", episodeId, "noteTemplate"]
}

export function useEpisodeNoteTemplate(episodeId: string) {
  return useSuspenseQuery({
    queryKey: useEpisodeNoteTemplateQueryKey(episodeId),
    queryFn: ({ queryKey }) => {
      const [_, episodeId] = queryKey
      return sessionApiClient
        .get(`/publishing/v2/episodes/${episodeId}/note_template`)
        .catch((json: unknown) => json)
    },
    select: (response: { data: NoteTemplateAPIRecord }) =>
      response.data.attributes.template,
    staleTime: Infinity,
  })
}
